import React, { useState, useEffect, useRef } from "react";
import FlashCard from "./FlashCard";
import { updateCardInterval } from "../utils/spacedRepetition";
import { CONFIG } from "../config";
import AllSentencesList from "./AllSentencesList";
export default function CardPlayer({ cards, setCards, deckName }) {
  const [sessionCards, setSessionCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoMode, setAutoMode] = useState(CONFIG.autoMode);
  const [triggerFlip, setTriggerFlip] = useState(false);
  const [animateKey, setAnimateKey] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef({});
  const timeouts = useRef([]);

  useEffect(() => {
    let session = [];

    if (CONFIG.mode === "random") {
      session = [...cards]
        .sort(() => Math.random() - 0.5)
        .slice(0, CONFIG.maxSessionCards);
    } else {
      let pool = cards
        .filter((c) => c.unlocked)
        .slice(0, CONFIG.maxActivePoolSize);

      if (CONFIG.randomisation === "shuffle") {
        pool = pool.sort(() => Math.random() - 0.5);
      } else if (CONFIG.randomisation === "newest-first") {
        pool = pool.sort((a, b) => b.lastSeen - a.lastSeen);
      }

      session = pool.slice(0, CONFIG.maxSessionCards);
    }

    setSessionCards(session);
    setCurrentIndex(0);
    setTriggerFlip(false);
    setAnimateKey(0);
  }, [cards]);

  useEffect(() => {
    stopAll();
    setTriggerFlip(false);

    const card = sessionCards[currentIndex];
    if (autoMode) {
      startAutoPlayback(card);
    } else if (card?.type === "audioText" && card.questionAudio) {
      const q = playAudio(`/audio/${card.questionAudio}`);
      audioRef.current.q = q;
    }

    return () => stopAll();
  }, [currentIndex, autoMode]);

  const stopAll = () => {
    Object.values(audioRef.current).forEach((audio) => {
      if (audio) {
        audio.pause();
        audio.src = "";
      }
    });
    audioRef.current = {};
    timeouts.current.forEach(clearTimeout);
    timeouts.current = [];
  };

  const playAudio = (src) => {
    if (isMuted) return { play: () => {}, pause: () => {}, src: "" }; // stub
    const audio = new Audio(src);
    audio.play().catch((err) => {
      if (err.name !== "AbortError") console.error("Audio error:", err);
    });
    return audio;
  };

  const calculateDelay = (text) => {
    const wordCount = text.trim().split(/\s+/).length;
    const letterCount = text.replace(/[^a-zA-Z]/g, "").length;
    return (
      (wordCount * CONFIG.delayScale.perWord +
        letterCount * CONFIG.delayScale.perLetter) *
      1000
    );
  };

  const startAutoPlayback = (card) => {
    if (!card || card.type !== "audioText") return;

    const q = playAudio(`/audio/${card.questionAudio}`);
    const a = playAudio(`/audio/${card.answerAudio}`);
    a.pause();
    audioRef.current = { q, a };

    q.onended = () => {
      const delay = calculateDelay(card.text.question);
      const t1 = setTimeout(() => {
        a.play().catch((err) => {
          if (err.name !== "AbortError") {
            console.error("Answer audio error:", err);
          }
        });
        a.onended = () => {
          setTriggerFlip(true);
          const t2 = setTimeout(() => {
            if (currentIndex + 1 < sessionCards.length && autoMode) {
              fadeAndAdvance(currentIndex + 1);
            }
          }, CONFIG.delayScale.betweenCards * 1000);
          timeouts.current.push(t2);
        };
      }, delay);
      timeouts.current.push(t1);
    };
  };

  const fadeAndAdvance = (nextIndex) => {
    setAnimateKey((prev) => prev + 1);
    setTimeout(() => {
      setCurrentIndex(nextIndex);
    }, 150); // matches fade-out duration
  };

  const handleNext = (correct = true) => {
    stopAll();
    const updated = [...cards];
    const card = sessionCards[currentIndex];
    const index = cards.findIndex((c) => c.id === card.id);
    updated[index] = updateCardInterval({ ...card }, correct);
    setCards(updated);
    localStorage.setItem(`deckProgress_${deckName}`, JSON.stringify(updated));
    if (currentIndex + 1 < sessionCards.length) {
      fadeAndAdvance(currentIndex + 1);
    }
  };

  const toggleAuto = () => {
    setAutoMode((prev) => {
      const next = !prev;
      if (!next) stopAll();
      return next;
    });
  };

  const card = sessionCards[currentIndex];
  if (!card) return <p>No cards ready.</p>;

  return (
    <div>
      <div>
        {/* <progress value={currentIndex + 1} max={sessionCards.length}></progress> */}
        {/* <span>
          {" "}
          {currentIndex + 1} / {sessionCards.length}
        </span> */}
      </div>

      <FlashCard
        card={card}
        autoMode={autoMode}
        triggerFlip={triggerFlip}
        animateKey={animateKey}
        isMuted={isMuted}
      />

      {CONFIG.showDebugStats && (
        <div style={{ fontSize: "0.8em", color: "#666" }}>
          <p>Repetitions: {card.repetitions}</p>
          <p>Ease: {card.ease}</p>
          <p>Interval: {card.interval} day(s)</p>
          <p>Next Review: {new Date(card.nextReview).toLocaleString()}</p>
        </div>
      )}

      <div>
        <button onClick={() => handleNext(true)}>Next</button>
        {/* <button
          onClick={() => handleNext(false)}
          style={{ marginLeft: "10px" }}
        >
          I didn’t know this
        </button> */}
        {card.type === "audioText" && (
          <button onClick={toggleAuto}>AUTO: {autoMode ? "ON" : "OFF"}</button>
        )}
        <button
          onClick={() => setIsMuted(!isMuted)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0.5rem",
            fontSize: "1.2rem",
          }}
          aria-label={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? (
            // Muted Icon
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 9L5 12H2V12H5L9 15V9Z" fill="currentColor" />
              <path d="M16 8L8 16" stroke="currentColor" strokeWidth="2" />
              <path d="M8 8L16 16" stroke="currentColor" strokeWidth="2" />
            </svg>
          ) : (
            // Unmuted Icon
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M9 9L5 12H2V12H5L9 15V9Z" fill="currentColor" />
              <path
                d="M14 7C16.5 9 16.5 15 14 17"
                stroke="currentColor"
                strokeWidth="2"
              />
              <path
                d="M17 4C21 8 21 16 17 20"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          )}
        </button>
      </div>
      <AllSentencesList cards={sessionCards} isMuted={isMuted} />
    </div>
  );
}
